// LocalStorage Service for BlinD Application
// Provides persistent storage for all application data without external database

const STORAGE_KEYS = {
  CANDIDATES: 'blind_candidates',
  EMPLOYERS: 'blind_employers',
  MATCHES: 'blind_matches',
  CONVERSATIONS: 'blind_conversations',
  MESSAGES: 'blind_messages',
  USER_SESSION: 'blind_user_session',
  PREFERENCES: 'blind_preferences',
  PROFILES: 'blind_profiles',
  OPPORTUNITIES: 'blind_opportunities',
  REVEAL_REQUESTS: 'blind_reveal_requests',
  INTERVIEWS: 'blind_interviews'
};

// Helper function to safely parse JSON from localStorage
const getFromStorage = (key, defaultValue = []) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return defaultValue;
  }
};

// Helper function to safely save to localStorage
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
    return false;
  }
};

// Generate unique ID
const generateId = () => {
  return `${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
};

// ============ USER SESSION ============
export const userSession = {
  get: () => getFromStorage(STORAGE_KEYS?.USER_SESSION, null),
  
  set: (userData) => {
    const session = {
      ...userData,
      lastActive: new Date()?.toISOString()
    };
    return saveToStorage(STORAGE_KEYS?.USER_SESSION, session);
  },
  
  clear: () => localStorage.removeItem(STORAGE_KEYS?.USER_SESSION),
  
  isAuthenticated: () => {
    const session = userSession?.get();
    return session !== null;
  },
  
  getUserType: () => {
    const session = userSession?.get();
    return session?.userType || null;
  }
};

// ============ CANDIDATES ============
export const candidates = {
  getAll: () => getFromStorage(STORAGE_KEYS?.CANDIDATES, []),
  
  getById: (id) => {
    const all = candidates?.getAll();
    return all?.find(c => c?.id === id) || null;
  },
  
  create: (candidateData) => {
    const all = candidates?.getAll();
    const newCandidate = {
      id: generateId(),
      ...candidateData,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    all?.push(newCandidate);
    saveToStorage(STORAGE_KEYS?.CANDIDATES, all);
    return newCandidate;
  },
  
  update: (id, updates) => {
    const all = candidates?.getAll();
    const index = all?.findIndex(c => c?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        ...updates,
        updatedAt: new Date()?.toISOString()
      };
      saveToStorage(STORAGE_KEYS?.CANDIDATES, all);
      return all?.[index];
    }
    return null;
  },
  
  delete: (id) => {
    const all = candidates?.getAll();
    const filtered = all?.filter(c => c?.id !== id);
    return saveToStorage(STORAGE_KEYS?.CANDIDATES, filtered);
  },
  
  search: (filters) => {
    let results = candidates?.getAll();
    
    if (filters?.industry && filters?.industry !== 'all') {
      results = results?.filter(c => c?.industry === filters?.industry);
    }
    
    if (filters?.location && filters?.location !== 'all') {
      results = results?.filter(c => c?.location?.includes(filters?.location));
    }
    
    if (filters?.seniority && filters?.seniority !== 'all') {
      results = results?.filter(c => c?.seniority === filters?.seniority);
    }
    
    if (filters?.keywords) {
      const keywords = filters?.keywords?.toLowerCase();
      results = results?.filter(c => 
        c?.title?.toLowerCase()?.includes(keywords) ||
        c?.summary?.toLowerCase()?.includes(keywords) ||
        c?.skills?.some(s => s?.toLowerCase()?.includes(keywords))
      );
    }
    
    return results;
  }
};

// ============ EMPLOYERS ============
export const employers = {
  getAll: () => getFromStorage(STORAGE_KEYS?.EMPLOYERS, []),
  
  getById: (id) => {
    const all = employers?.getAll();
    return all?.find(e => e?.id === id) || null;
  },
  
  create: (employerData) => {
    const all = employers?.getAll();
    const newEmployer = {
      id: generateId(),
      ...employerData,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    all?.push(newEmployer);
    saveToStorage(STORAGE_KEYS?.EMPLOYERS, all);
    return newEmployer;
  },
  
  update: (id, updates) => {
    const all = employers?.getAll();
    const index = all?.findIndex(e => e?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        ...updates,
        updatedAt: new Date()?.toISOString()
      };
      saveToStorage(STORAGE_KEYS?.EMPLOYERS, all);
      return all?.[index];
    }
    return null;
  }
};

// ============ PROFILES ============
export const profiles = {
  getAll: () => getFromStorage(STORAGE_KEYS?.PROFILES, []),
  
  getByUserId: (userId) => {
    const all = profiles?.getAll();
    return all?.filter(p => p?.userId === userId);
  },
  
  getCurrent: (userId) => {
    const userProfiles = profiles?.getByUserId(userId);
    return userProfiles?.find(p => p?.isCurrent) || userProfiles?.[0] || null;
  },
  
  create: (profileData) => {
    const all = profiles?.getAll();
    const newProfile = {
      id: generateId(),
      ...profileData,
      createdAt: new Date()?.toISOString(),
      isCurrent: true
    };
    
    // Set other profiles as not current
    const updated = all?.map(p => 
      p?.userId === profileData?.userId ? { ...p, isCurrent: false } : p
    );
    updated?.push(newProfile);
    
    saveToStorage(STORAGE_KEYS?.PROFILES, updated);
    return newProfile;
  },
  
  update: (id, updates) => {
    const all = profiles?.getAll();
    const index = all?.findIndex(p => p?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        ...updates,
        updatedAt: new Date()?.toISOString()
      };
      saveToStorage(STORAGE_KEYS?.PROFILES, all);
      return all?.[index];
    }
    return null;
  },
  
  setAsCurrent: (id) => {
    const all = profiles?.getAll();
    const profile = all?.find(p => p?.id === id);
    if (!profile) return null;
    
    const updated = all?.map(p => ({
      ...p,
      isCurrent: p?.id === id && p?.userId === profile?.userId
    }));
    
    saveToStorage(STORAGE_KEYS?.PROFILES, updated);
    return updated?.find(p => p?.id === id);
  }
};

// ============ OPPORTUNITIES ============
export const opportunities = {
  getAll: () => getFromStorage(STORAGE_KEYS?.OPPORTUNITIES, []),
  
  getById: (id) => {
    const all = opportunities?.getAll();
    return all?.find(o => o?.id === id) || null;
  },
  
  create: (opportunityData) => {
    const all = opportunities?.getAll();
    const newOpportunity = {
      id: generateId(),
      ...opportunityData,
      createdAt: new Date()?.toISOString(),
      isActive: true
    };
    all?.push(newOpportunity);
    saveToStorage(STORAGE_KEYS?.OPPORTUNITIES, all);
    return newOpportunity;
  },
  
  search: (filters) => {
    let results = opportunities?.getAll()?.filter(o => o?.isActive);
    
    if (filters?.industry && filters?.industry !== 'all') {
      results = results?.filter(o => o?.industry === filters?.industry);
    }
    
    if (filters?.location && filters?.location !== 'all') {
      results = results?.filter(o => o?.location?.includes(filters?.location));
    }
    
    if (filters?.roleType && filters?.roleType !== 'all') {
      results = results?.filter(o => o?.roleType === filters?.roleType);
    }
    
    if (filters?.exceptionalOnly) {
      results = results?.filter(o => o?.isExceptional);
    }
    
    return results;
  }
};

// ============ MATCHES ============
export const matches = {
  getAll: () => getFromStorage(STORAGE_KEYS?.MATCHES, []),
  
  getById: (id) => {
    const all = matches?.getAll();
    return all?.find(m => m?.id === id) || null;
  },
  
  getByUserId: (userId) => {
    const all = matches?.getAll();
    return all?.filter(m => m?.candidateId === userId || m?.employerId === userId);
  },
  
  create: (matchData) => {
    const all = matches?.getAll();
    const newMatch = {
      id: generateId(),
      ...matchData,
      stage: 'Initial Connection',
      createdAt: new Date()?.toISOString(),
      messageCount: 0
    };
    all?.push(newMatch);
    saveToStorage(STORAGE_KEYS?.MATCHES, all);
    return newMatch;
  },
  
  update: (id, updates) => {
    const all = matches?.getAll();
    const index = all?.findIndex(m => m?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        ...updates,
        updatedAt: new Date()?.toISOString()
      };
      saveToStorage(STORAGE_KEYS?.MATCHES, all);
      return all?.[index];
    }
    return null;
  },
  
  updateStage: (id, stage) => {
    return matches?.update(id, { stage });
  },
  
  search: (filters) => {
    let results = matches?.getAll();
    
    if (filters?.stage && filters?.stage !== 'all') {
      results = results?.filter(m => m?.stage === filters?.stage);
    }
    
    if (filters?.industry && filters?.industry !== 'all') {
      results = results?.filter(m => m?.industry === filters?.industry);
    }
    
    if (filters?.search) {
      const search = filters?.search?.toLowerCase();
      results = results?.filter(m => 
        m?.title?.toLowerCase()?.includes(search) ||
        m?.description?.toLowerCase()?.includes(search)
      );
    }
    
    return results;
  }
};

// ============ CONVERSATIONS ============
export const conversations = {
  getAll: () => getFromStorage(STORAGE_KEYS?.CONVERSATIONS, []),
  
  getById: (id) => {
    const all = conversations?.getAll();
    return all?.find(c => c?.id === id) || null;
  },
  
  getByMatchId: (matchId) => {
    const all = conversations?.getAll();
    return all?.find(c => c?.matchId === matchId) || null;
  },
  
  getByUserId: (userId) => {
    const all = conversations?.getAll();
    return all?.filter(c => c?.participantIds?.includes(userId));
  },
  
  create: (conversationData) => {
    const all = conversations?.getAll();
    const newConversation = {
      id: generateId(),
      ...conversationData,
      createdAt: new Date()?.toISOString(),
      lastMessageTime: new Date()?.toISOString(),
      unreadCount: 0
    };
    all?.push(newConversation);
    saveToStorage(STORAGE_KEYS?.CONVERSATIONS, all);
    return newConversation;
  },
  
  update: (id, updates) => {
    const all = conversations?.getAll();
    const index = all?.findIndex(c => c?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        ...updates
      };
      saveToStorage(STORAGE_KEYS?.CONVERSATIONS, all);
      return all?.[index];
    }
    return null;
  },
  
  markAsRead: (id, userId) => {
    return conversations?.update(id, { unreadCount: 0 });
  }
};

// ============ MESSAGES ============
export const messages = {
  getAll: () => getFromStorage(STORAGE_KEYS?.MESSAGES, []),
  
  getByConversationId: (conversationId) => {
    const all = messages?.getAll();
    return all?.filter(m => m?.conversationId === conversationId)?.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  },
  
  create: (messageData) => {
    const all = messages?.getAll();
    const newMessage = {
      id: generateId(),
      ...messageData,
      timestamp: new Date()?.toISOString(),
      isRead: false
    };
    all?.push(newMessage);
    saveToStorage(STORAGE_KEYS?.MESSAGES, all);
    
    // Update conversation last message
    const conversation = conversations?.getById(messageData?.conversationId);
    if (conversation) {
      conversations?.update(messageData?.conversationId, {
        lastMessage: messageData?.content,
        lastMessageTime: newMessage?.timestamp,
        unreadCount: (conversation?.unreadCount || 0) + 1
      });
    }
    
    return newMessage;
  },
  
  markAsRead: (conversationId, userId) => {
    const all = messages?.getAll();
    const updated = all?.map(m => 
      m?.conversationId === conversationId && m?.senderId !== userId
        ? { ...m, isRead: true }
        : m
    );
    return saveToStorage(STORAGE_KEYS?.MESSAGES, updated);
  }
};

// ============ REVEAL REQUESTS ============
export const revealRequests = {
  getAll: () => getFromStorage(STORAGE_KEYS?.REVEAL_REQUESTS, []),
  
  getByMatchId: (matchId) => {
    const all = revealRequests?.getAll();
    return all?.find(r => r?.matchId === matchId) || null;
  },
  
  create: (requestData) => {
    const all = revealRequests?.getAll();
    const newRequest = {
      id: generateId(),
      ...requestData,
      status: 'pending',
      createdAt: new Date()?.toISOString()
    };
    all?.push(newRequest);
    saveToStorage(STORAGE_KEYS?.REVEAL_REQUESTS, all);
    return newRequest;
  },
  
  update: (id, updates) => {
    const all = revealRequests?.getAll();
    const index = all?.findIndex(r => r?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        ...updates,
        updatedAt: new Date()?.toISOString()
      };
      saveToStorage(STORAGE_KEYS?.REVEAL_REQUESTS, all);
      return all?.[index];
    }
    return null;
  },
  
  approve: (id) => {
    return revealRequests?.update(id, { status: 'approved' });
  },
  
  reject: (id, reason) => {
    return revealRequests?.update(id, { status: 'rejected', rejectionReason: reason });
  }
};

// ============ INTERVIEWS ============
export const interviews = {
  getAll: () => getFromStorage(STORAGE_KEYS?.INTERVIEWS, []),
  
  getByMatchId: (matchId) => {
    const all = interviews?.getAll();
    return all?.filter(i => i?.matchId === matchId);
  },
  
  getByUserId: (userId) => {
    const all = interviews?.getAll();
    return all?.filter(i => i?.candidateId === userId || i?.employerId === userId);
  },
  
  create: (interviewData) => {
    const all = interviews?.getAll();
    const newInterview = {
      id: generateId(),
      ...interviewData,
      status: 'scheduled',
      createdAt: new Date()?.toISOString()
    };
    all?.push(newInterview);
    saveToStorage(STORAGE_KEYS?.INTERVIEWS, all);
    return newInterview;
  },
  
  update: (id, updates) => {
    const all = interviews?.getAll();
    const index = all?.findIndex(i => i?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        ...updates
      };
      saveToStorage(STORAGE_KEYS?.INTERVIEWS, all);
      return all?.[index];
    }
    return null;
  }
};

// ============ PREFERENCES ============
export const preferences = {
  get: (userId) => {
    const all = getFromStorage(STORAGE_KEYS?.PREFERENCES, {});
    return all?.[userId] || {};
  },
  
  set: (userId, prefs) => {
    const all = getFromStorage(STORAGE_KEYS?.PREFERENCES, {});
    all[userId] = {
      ...all?.[userId],
      ...prefs,
      updatedAt: new Date()?.toISOString()
    };
    return saveToStorage(STORAGE_KEYS?.PREFERENCES, all);
  },
  
  update: (userId, updates) => {
    return preferences?.set(userId, updates);
  }
};

// ============ UTILITY FUNCTIONS ============
export const clearAllData = () => {
  Object.values(STORAGE_KEYS)?.forEach(key => {
    localStorage.removeItem(key);
  });
};

export const exportData = () => {
  const data = {};
  Object.entries(STORAGE_KEYS)?.forEach(([name, key]) => {
    data[name] = getFromStorage(key, null);
  });
  return data;
};

export const importData = (data) => {
  Object.entries(data)?.forEach(([name, value]) => {
    const key = STORAGE_KEYS?.[name];
    if (key && value !== null) {
      saveToStorage(key, value);
    }
  });
};

// ============ NOTIFICATIONS ============
export const notifications = {
  getAll: () => getFromStorage(STORAGE_KEYS?.NOTIFICATIONS, []),
  
  getById: (id) => {
    const all = notifications?.getAll();
    return all?.find(n => n?.id === id) || null;
  },
  
  getByUserId: (userId) => {
    const all = notifications?.getAll();
    return all?.filter(n => n?.userId === userId)?.sort((a, b) => 
      new Date(b?.createdAt) - new Date(a?.createdAt)
    );
  },
  
  getUnreadCount: (userId) => {
    const userNotifications = notifications?.getByUserId(userId);
    return userNotifications?.filter(n => !n?.isRead)?.length || 0;
  },
  
  create: (notificationData) => {
    const all = notifications?.getAll();
    const newNotification = {
      id: generateId(),
      ...notificationData,
      isRead: false,
      createdAt: new Date()?.toISOString()
    };
    all?.push(newNotification);
    saveToStorage(STORAGE_KEYS?.NOTIFICATIONS, all);
    return newNotification;
  },
  
  markAsRead: (id) => {
    const all = notifications?.getAll();
    const index = all?.findIndex(n => n?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        isRead: true,
        readAt: new Date()?.toISOString()
      };
      saveToStorage(STORAGE_KEYS?.NOTIFICATIONS, all);
      return all?.[index];
    }
    return null;
  },
  
  markAllAsRead: (userId) => {
    const all = notifications?.getAll();
    const updated = all?.map(n => 
      n?.userId === userId && !n?.isRead
        ? { ...n, isRead: true, readAt: new Date()?.toISOString() }
        : n
    );
    saveToStorage(STORAGE_KEYS?.NOTIFICATIONS, updated);
    return updated?.filter(n => n?.userId === userId);
  },
  
  delete: (id) => {
    const all = notifications?.getAll();
    const filtered = all?.filter(n => n?.id !== id);
    return saveToStorage(STORAGE_KEYS?.NOTIFICATIONS, filtered);
  },
  
  deleteMultiple: (ids) => {
    const all = notifications?.getAll();
    const filtered = all?.filter(n => !ids?.includes(n?.id));
    return saveToStorage(STORAGE_KEYS?.NOTIFICATIONS, filtered);
  },
  
  filterByType: (userId, type) => {
    const userNotifications = notifications?.getByUserId(userId);
    if (type === 'all') return userNotifications;
    return userNotifications?.filter(n => n?.type === type);
  },
  
  filterByPriority: (userId, priority) => {
    const userNotifications = notifications?.getByUserId(userId);
    if (priority === 'all') return userNotifications;
    return userNotifications?.filter(n => n?.priority === priority);
  }
};

// ============ SAVED FILTERS ============
export const savedFilters = {
  getAll: (userId) => {
    const all = getFromStorage(STORAGE_KEYS?.SAVED_FILTERS, []);
    return all?.filter(f => f?.userId === userId);
  },

  getById: (id) => {
    const all = getFromStorage(STORAGE_KEYS?.SAVED_FILTERS, []);
    return all?.find(f => f?.id === id) || null;
  },

  create: (userId, name, filters, context = 'matches') => {
    const all = getFromStorage(STORAGE_KEYS?.SAVED_FILTERS, []);
    const newFilter = {
      id: generateId(),
      userId,
      name,
      filters,
      context,
      createdAt: new Date()?.toISOString()
    };
    all?.push(newFilter);
    saveToStorage(STORAGE_KEYS?.SAVED_FILTERS, all);
    return newFilter;
  },

  delete: (id) => {
    const all = getFromStorage(STORAGE_KEYS?.SAVED_FILTERS, []);
    const filtered = all?.filter(f => f?.id !== id);
    return saveToStorage(STORAGE_KEYS?.SAVED_FILTERS, filtered);
  },

  update: (id, updates) => {
    const all = getFromStorage(STORAGE_KEYS?.SAVED_FILTERS, []);
    const index = all?.findIndex(f => f?.id === id);
    if (index !== -1) {
      all[index] = {
        ...all?.[index],
        ...updates,
        updatedAt: new Date()?.toISOString()
      };
      saveToStorage(STORAGE_KEYS?.SAVED_FILTERS, all);
      return all?.[index];
    }
    return null;
  }
};

// ============ SEARCH HISTORY ============
export const searchHistory = {
  getAll: (userId, context = 'matches') => {
    const all = getFromStorage(STORAGE_KEYS?.SEARCH_HISTORY, []);
    return all
      ?.filter(h => h?.userId === userId && h?.context === context)
      ?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))
      ?.slice(0, 20); // Keep last 20 searches
  },

  add: (userId, term, context = 'matches') => {
    if (!term?.trim()) return null;
    
    const all = getFromStorage(STORAGE_KEYS?.SEARCH_HISTORY, []);
    
    // Remove duplicate if exists
    const filtered = all?.filter(
      h => !(h?.userId === userId && h?.term === term && h?.context === context)
    );
    
    const newEntry = {
      id: generateId(),
      userId,
      term: term?.trim(),
      context,
      timestamp: new Date()?.toISOString()
    };
    
    filtered?.unshift(newEntry);
    
    // Keep only last 50 entries per user per context
    const userEntries = filtered?.filter(h => h?.userId === userId && h?.context === context);
    const otherEntries = filtered?.filter(h => !(h?.userId === userId && h?.context === context));
    const trimmed = [...userEntries?.slice(0, 50), ...otherEntries];
    
    saveToStorage(STORAGE_KEYS?.SEARCH_HISTORY, trimmed);
    return newEntry;
  },

  clear: (userId, context = 'matches') => {
    const all = getFromStorage(STORAGE_KEYS?.SEARCH_HISTORY, []);
    const filtered = all?.filter(h => !(h?.userId === userId && h?.context === context));
    return saveToStorage(STORAGE_KEYS?.SEARCH_HISTORY, filtered);
  },

  delete: (id) => {
    const all = getFromStorage(STORAGE_KEYS?.SEARCH_HISTORY, []);
    const filtered = all?.filter(h => h?.id !== id);
    return saveToStorage(STORAGE_KEYS?.SEARCH_HISTORY, filtered);
  }
};

export default {
  userSession,
  candidates,
  employers,
  profiles,
  opportunities,
  matches,
  conversations,
  messages,
  revealRequests,
  interviews,
  preferences,
  clearAllData,
  exportData,
  importData
};